// Separate from useMiniERP.js on purpose — EDI is a new cross-account
// subsystem with its own backend routes (/minierp/edi/*), so keeping it in
// its own hook means zero risk to the existing hook you've been hand-tuning.
import { useSelector } from "react-redux";
import useFetch from "../../../hooks/useFetch";

export function useMiniERPEDI() {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData] = useFetch();
  const token = authState.token;

  // ====================== CONTACTS (Favourites) ======================
  const fetchContacts = async (type = null) => {
    const config = {
      url: type ? `/minierp/edi/contacts?type=${type}` : "/minierp/edi/contacts",
      method: "get",
      headers: { Authorization: token }
    };
    return fetchData(config, { silent: true });
  };

  const createContact = async (payload) => {
    const config = { url: "/minierp/edi/contacts", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const deleteContact = async (id) => {
    const config = { url: `/minierp/edi/contacts/${id}`, method: "delete", headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  // ====================== BUSINESS CODE LOOKUP ======================
  const lookupBusinessCode = async (code) => {
    const config = { url: `/minierp/edi/lookup/${code}`, method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  // ====================== EDI TRANSACTIONS ======================
  const fetchEDITransactions = async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const config = {
      url: params ? `/minierp/edi/transactions?${params}` : "/minierp/edi/transactions",
      method: "get",
      headers: { Authorization: token }
    };
    return fetchData(config, { silent: true });
  };

  const fetchEDITransactionById = async (id) => {
    const config = { url: `/minierp/edi/transactions/${id}`, method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const createEDITransaction = async (payload) => {
    const config = { url: "/minierp/edi/transactions", method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const updateEDITransaction = async (id, payload) => {
    const config = { url: `/minierp/edi/transactions/${id}`, method: "put", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const submitOtp = async (id, enteredOtp, signatureUrl, signatureWaived) => {
    const config = {
      url: `/minierp/edi/transactions/${id}/otp`, method: "post",
      data: { enteredOtp, signatureUrl, signatureWaived },
      headers: { Authorization: token }
    };
    return fetchData(config, { silent: false });
  };

  const rejectEDITransaction = async (id, reason) => {
    const config = { url: `/minierp/edi/transactions/${id}/reject`, method: "post", data: { reason }, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const attachSignature = async (id, signatureBase64) => {
    const config = { url: `/minierp/edi/transactions/${id}/signature`, method: "post", data: { signatureBase64 }, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const fetchTrasCases = async (status = null) => {
    const config = {
      url: status ? `/minierp/edi/tras/cases?status=${status}` : "/minierp/edi/tras/cases",
      method: "get", headers: { Authorization: token }
    };
    return fetchData(config, { silent: true });
  };

  const getOrCreateTrasCase = async (pettyTransactionId) => {
    const config = { url: `/minierp/edi/tras/cases/from-transaction/${pettyTransactionId}`, method: "post", headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const fetchTrasCaseDetail = async (caseId) => {
    const config = { url: `/minierp/edi/tras/cases/${caseId}`, method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const createTrasInteraction = async (caseId, payload) => {
    const config = { url: `/minierp/edi/tras/cases/${caseId}/interactions`, method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const createTrasVisit = async (caseId, payload) => {
    const config = { url: `/minierp/edi/tras/cases/${caseId}/visits`, method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const createTrasCollection = async (caseId, payload) => {
    const config = { url: `/minierp/edi/tras/cases/${caseId}/collections`, method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const createTrasPTP = async (caseId, payload) => {
    const config = { url: `/minierp/edi/tras/cases/${caseId}/ptp`, method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const fetchTrasPTPs = async (caseId) => {
    const config = { url: `/minierp/edi/tras/cases/${caseId}/ptp`, method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const createTrasCostTime = async (caseId, payload) => {
    const config = { url: `/minierp/edi/tras/cases/${caseId}/costtime`, method: "post", data: payload, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  const fetchTrasCostTimeSummary = async (caseId) => {
    const config = { url: `/minierp/edi/tras/cases/${caseId}/costtime`, method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const fetchTrasRecoveryRoi = async (caseId) => {
    const config = { url: `/minierp/edi/tras/cases/${caseId}/roi`, method: "get", headers: { Authorization: token } };
    return fetchData(config, { silent: true });
  };

  const createTrasDocument = async (caseId, docType, amount) => {
    const config = { url: `/minierp/edi/tras/cases/${caseId}/documents`, method: "post", data: { docType, amount }, headers: { Authorization: token } };
    return fetchData(config, { silent: false });
  };

  return {
    fetchContacts, createContact, deleteContact,
    lookupBusinessCode,
    fetchEDITransactions, fetchEDITransactionById, createEDITransaction, updateEDITransaction,
    submitOtp, rejectEDITransaction, attachSignature,
    fetchTrasCases, getOrCreateTrasCase, fetchTrasCaseDetail,
    createTrasInteraction, createTrasVisit, createTrasCollection,
    createTrasPTP, fetchTrasPTPs,
    createTrasCostTime, fetchTrasCostTimeSummary,
    fetchTrasRecoveryRoi, createTrasDocument,
  };
}
